import React, { useState } from 'react';
import { smsReceiverApi } from '../api/smsApi';
import { pickContacts, normalizePhone } from '../utils/contactPicker';

// ============================================================
// [CONTACT_BULK_V1] 폰 연락처 여러 명 일괄 등록 모달
//
// 흐름:
//   1) 열리면 바로 폰 연락처 선택 창을 띄운다
//   2) 고른 사람들을 목록으로 보여준다 (이미 등록된 번호는 회색 + 체크 해제)
//   3) 그룹명·구분을 한 번 정하면 전원에게 같은 값으로 적용된다
//   4) [등록] → API 가 한 건씩만 받으므로 순서대로 보내며 진행률을 표시한다
//
// 연관 파일:
//   utils/contactPicker.js   연락처 읽기 + 번호 형식 정리
//   api/smsApi.js            smsReceiverApi.create (한 건씩 등록)
//   pages/ReceiverManagePage.jsx  이 모달을 여는 곳. 기존 목록(existing)을 넘겨준다
//
// ※ components/ReceiverManageModal.jsx 는 EstateEditor 에서도 쓰이므로
//   건드리지 않고 이 파일을 따로 만들었다.
// ============================================================

// 번호 비교용 — 하이픈·공백을 뺀 숫자만 남긴다.
// 기존 데이터에 "01012345678" 처럼 하이픈 없이 저장된 것도 있어
// 그대로 비교하면 같은 번호를 다른 번호로 오인한다.
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

export default function ContactBulkImportModal({ open, onClose, onRefresh, existing = [], groups = [] }) {
  const [rows, setRows]       = useState([]);      // {name, phone, dup, checked}
  const [groupName, setGroup] = useState("");
  const [category, setCat]    = useState("고객");
  const [phase, setPhase]     = useState("idle");  // idle | picking | listed | saving | done
  const [progress, setProg]   = useState({ done: 0, total: 0, fail: 0 });
  const [msg, setMsg]         = useState("");

  if (!open) return null;

  // ── 연락처 불러오기 ────────────────────────────────────
  const handlePick = async () => {
    setPhase("picking"); setMsg("");
    const picked = await pickContacts(true);   // true = 여러 명
    if (!picked.length) {
      setPhase("idle");
      setMsg("선택된 연락처가 없습니다.");
      return;
    }

    // 이미 등록된 번호 집합 (숫자만으로 비교)
    const known = new Set(existing.map(r => digitsOnly(r.phone)));

    // 이번에 고른 것들 안에서의 중복도 걸러낸다 (같은 사람을 두 번 고른 경우)
    const seen = new Set();
    const list = [];
    for (const c of picked) {
      const d = digitsOnly(c.phone);
      if (!d) continue;                 // 번호 없는 연락처는 제외
      if (seen.has(d)) continue;        // 이번 선택 안에서의 중복
      seen.add(d);
      const dup = known.has(d);
      list.push({
        name: c.name || "",
        phone: c.phone,
        dup,                            // 이미 등록된 번호인가
        checked: !dup,                  // 중복이면 기본 해제
      });
    }
    setRows(list);
    setPhase("listed");
  };

  const toggle = (i) =>
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, checked: !r.checked } : r));

  const setField = (i, key, val) =>
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const selected = rows.filter(r => r.checked);
  const dupCount = rows.filter(r => r.dup).length;

  // ── 일괄 등록 ─────────────────────────────────────────
  const handleSave = async () => {
    const targets = rows.filter(r => r.checked && r.phone.trim());
    if (!targets.length) { setMsg("등록할 대상이 없습니다."); return; }

    // 그룹명을 비우면 현재 시각으로 자동 생성 — 기존 등록 모달과 같은 규칙
    let finalGroup = groupName.trim();
    if (!finalGroup) {
      const n = new Date();
      const p = (x) => String(x).padStart(2, "0");
      finalGroup = `${n.getFullYear()}-${p(n.getMonth()+1)}-${p(n.getDate())} ${p(n.getHours())}:${p(n.getMinutes())}`;
    }

    setPhase("saving");
    setProg({ done: 0, total: targets.length, fail: 0 });
    let fail = 0;
    const failNames = [];

    // API 가 한 건씩만 받으므로 순서대로 보낸다.
    // 한 건 실패해도 멈추지 않고 끝까지 진행한 뒤 결과를 알려준다.
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      try {
        const res = await smsReceiverApi.create({
          name: t.name || t.phone,
          phone: t.phone,
          group_name: finalGroup,
          category,
          memo: "",
        });
        if (!res?.ok) { fail++; failNames.push(t.name || t.phone); }
      } catch (e) {
        fail++; failNames.push(t.name || t.phone);
      }
      setProg({ done: i + 1, total: targets.length, fail });
    }

    setPhase("done");
    setMsg(fail === 0
      ? `${targets.length}명을 등록했습니다.`
      : `${targets.length - fail}명 등록, ${fail}명 실패 (${failNames.slice(0, 3).join(", ")}${failNames.length > 3 ? " 외" : ""})`);
    onRefresh && onRefresh();
  };

  const close = () => {
    setRows([]); setGroup(""); setCat("고객");
    setPhase("idle"); setMsg(""); setProg({ done: 0, total: 0, fail: 0 });
    onClose();
  };

  // ── 스타일 ────────────────────────────────────────────
  const inputStyle = {
    width: "100%", padding: "8px 10px", border: "1px solid #cbd5e0",
    borderRadius: "6px", fontSize: "14px", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: "12px", fontWeight: 600,
    color: "#718096", marginBottom: "4px",
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: "480px", width: "94%" }}>
        <div className="modal-header">
          <h3>📱 연락처 일괄 등록</h3>
          <button className="close-x" onClick={close}>&times;</button>
        </div>

        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>

          {/* 1단계: 연락처 고르기 */}
          {phase === "idle" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                폰 연락처에서 여러 명을 한 번에 선택해 등록합니다.
              </p>
              <button
                onClick={handlePick}
                style={{ padding: "12px 24px", background: "#0369a1", color: "#fff",
                         border: "none", borderRadius: "8px", fontSize: "15px",
                         fontWeight: 600, cursor: "pointer" }}>
                연락처 선택하기
              </button>
              {msg && <p style={{ marginTop: "14px", fontSize: "13px", color: "#dc2626" }}>{msg}</p>}
            </div>
          )}

          {phase === "picking" && (
            <p style={{ textAlign: "center", padding: "30px 0", color: "#64748b" }}>
              연락처 창을 여는 중...
            </p>
          )}

          {/* 2단계: 목록 확인 + 그룹/구분 지정 */}
          {(phase === "listed" || phase === "saving" || phase === "done") && (
            <>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>그룹명 (비우면 현재 시각으로 자동 생성)</label>
                <input
                  style={inputStyle} value={groupName} list="bulk-group-list"
                  onChange={e => setGroup(e.target.value)}
                  placeholder="예: 봉천동 임대인"
                  disabled={phase !== "listed"}
                />
                <datalist id="bulk-group-list">
                  {groups.filter(Boolean).map((g, i) => <option key={i} value={g} />)}
                </datalist>
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>구분 (선택한 전원에게 적용)</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  {["고객", "직원", "거래처", "기타"].map(c => (
                    <button
                      key={c} onClick={() => setCat(c)} disabled={phase !== "listed"}
                      style={{
                        flex: 1, padding: "7px 0", fontSize: "13px", cursor: "pointer",
                        borderRadius: "6px",
                        border: category === c ? "1px solid #2563eb" : "1px solid #cbd5e1",
                        background: category === c ? "#2563eb" : "#fff",
                        color: category === c ? "#fff" : "#475569",
                        fontWeight: category === c ? 600 : 400,
                      }}>{c}</button>
                  ))}
                </div>
              </div>

              <div style={{ fontSize: "13px", color: "#475569", marginBottom: "8px" }}>
                {rows.length}명 중 <strong>{selected.length}명 선택</strong>
                {dupCount > 0 && <span style={{ color: "#94a3b8" }}> · 이미 등록됨 {dupCount}명</span>}
              </div>

              {/* 선택 목록 */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                {rows.map((r, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "8px 10px",
                    borderBottom: i < rows.length - 1 ? "1px solid #f1f5f9" : "none",
                    background: r.dup ? "#f8fafc" : "#fff",
                    opacity: r.dup ? 0.65 : 1,
                  }}>
                    <input
                      type="checkbox" checked={r.checked}
                      disabled={phase !== "listed"}
                      onChange={() => toggle(i)}
                      style={{ width: "18px", height: "18px", flexShrink: 0 }}
                    />
                    <input
                      value={r.name} onChange={e => setField(i, "name", e.target.value)}
                      disabled={phase !== "listed"}
                      placeholder="이름"
                      style={{ ...inputStyle, width: "34%", padding: "5px 7px", fontSize: "13px" }}
                    />
                    <input
                      value={r.phone}
                      onChange={e => setField(i, "phone", normalizePhone(e.target.value))}
                      disabled={phase !== "listed"}
                      style={{ ...inputStyle, flex: 1, padding: "5px 7px", fontSize: "13px", minWidth: 0 }}
                    />
                    {r.dup && (
                      <span style={{ fontSize: "10px", color: "#94a3b8", flexShrink: 0 }}>등록됨</span>
                    )}
                  </div>
                ))}
              </div>

              {/* 진행 상황 */}
              {phase === "saving" && (
                <p style={{ marginTop: "12px", fontSize: "13px", color: "#475569", textAlign: "center" }}>
                  등록 중... {progress.done} / {progress.total}
                  {progress.fail > 0 && ` (실패 ${progress.fail})`}
                </p>
              )}
              {msg && phase === "done" && (
                <p style={{ marginTop: "12px", fontSize: "13px", color: "#475569", textAlign: "center" }}>
                  {msg}
                </p>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={close}>
            {phase === "done" ? "닫기" : "취소"}
          </button>
          {phase === "listed" && (
            <button className="btn-submit" onClick={handleSave} disabled={!selected.length}>
              {selected.length}명 등록
            </button>
          )}
        </div>
      </div>
    </div>
  );
}